import { useAuthenticatedFetch } from './apiService';

export interface ScorePromptType {
  id: number;
  name: string;
  prompt: string;
  listening: string;
  rapport: string;
  value_articulation: string;
  discovery: string;
  next_steps: string;
  created_at?: string;
  created_by?: string;
}

export const usePromptService = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return {
    createScorePrompt: async (
      data: Omit<ScorePromptType, 'id' | 'created_at' | 'created_by'>
    ): Promise<ScorePromptType> => {
      const res = await authenticatedFetch('/scenario/prompts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to create prompt');
      return await res.json();
    },

    fetchScorePrompts: async (): Promise<ScorePromptType[]> => {
      const res = await authenticatedFetch('/scenario/prompts');
      if (!res.ok) throw new Error('Failed to fetch prompts');
      return await res.json();
    },

    fetchScorePromptForScenario: async (
      scenarioId: number
    ): Promise<ScorePromptType> => {
      const res = await authenticatedFetch(`/scenario/prompts/by-scenario/${scenarioId}`);
      if (!res.ok) throw new Error('Failed to fetch prompt for scenario');
      return await res.json();
    },

    fetchScorePrompt: async (id: number): Promise<ScorePromptType> => {
      const res = await authenticatedFetch(`/scenario/prompts/${id}`);
      if (!res.ok) throw new Error('Prompt not found');
      return await res.json();
    },

    deleteScorePrompt: async (id: number): Promise<void> => {
      const res = await authenticatedFetch(`/scenario/prompts/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to delete prompt');
    },

    fetchAdminScenarioDetails: async (
      id: string | number
    ): Promise<ScorePromptType> => {
      const res = await authenticatedFetch(`/scenario/admin-details/${id}`);
      if (!res.ok) throw new Error('Failed to fetch admin scenario details');
      return await res.json();
    },

    updateScorePrompt: async (
      id: number,
      data: Partial<ScorePromptType>
    ): Promise<ScorePromptType> => {
      const res = await authenticatedFetch(`/scenario/prompts/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to update prompt');
      return await res.json();
    },

    updateScenarioVoice: async (
      scenarioId: string | number,
      voice: string
    ): Promise<void> => {
      const res = await authenticatedFetch(`/scenario/${scenarioId}/voice`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voice }),
      });
      if (!res.ok) throw new Error('Failed to update voice');
    },

    updateScenarioSystemPrompt: async (
      scenarioId: string | number,
      prompt: string
    ): Promise<void> => {
      const res = await authenticatedFetch(`/scenario/${scenarioId}/system-prompt`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
      });
      if (!res.ok) throw new Error('Failed to update system prompt');
    },
  };
};

export default usePromptService;